import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
} from 'react';

import type { PositionUserBalance } from '@infinex/asset-config';

import type { AccountAddress } from '../../../types';
import { mapHyperliquidAccountToBalance } from '../mappers';
import type { HyperliquidContextValueV2 } from '../types';

export interface HyperliquidMarginSummaryValue {
  getHyperliquidBalances(
    addresses: Array<AccountAddress>
  ): Array<PositionUserBalance>;
}

export const HyperliquidBalancesContext =
  createContext<HyperliquidMarginSummaryValue | null>(null);

export type HyperliquidBalancesProviderProps = {
  children: ReactNode;
  marginSummaryById: HyperliquidContextValueV2['marginSummaryById'];
  wallets: HyperliquidContextValueV2['wallets'];
};

export const HyperliquidBalancesProvider = memo(
  PrivateHyperliquidBalancesProvider,
  (prevProps, nextProps) => {
    return (
      // `children` must be part of the equality check: omitting it makes the
      // memo reuse its previous render (and thus a stale `children` element)
      // whenever `marginSummaryById`/`wallets` are referentially stable — which
      // they usually are (see `useReferentiallyStable` in `HyperliquidProvider`).
      // Consumers that drive state from ABOVE this provider and pass it down as
      // children (e.g. the Pro trade surface's `TradeFundingProvider`, which
      // toggles the deposit dialog's `isOpen` through here) would otherwise
      // never see their updates propagate — leaving the dialog undismissable.
      // During live-market churn the parent passes a stable `children`
      // reference, so this still short-circuits exactly as before.
      prevProps.children === nextProps.children &&
      prevProps.marginSummaryById === nextProps.marginSummaryById &&
      prevProps.wallets === nextProps.wallets
    );
  }
);

function PrivateHyperliquidBalancesProvider({
  children,
  marginSummaryById,
  wallets,
}: HyperliquidBalancesProviderProps) {
  const getHyperliquidBalances = useCallback(
    (addresses: AccountAddress[]) => {
      if (!wallets || !marginSummaryById) return [];

      // Get Arbitrum addresses from filtered addresses
      const arbitrumAddresses = addresses
        .filter((a) => a.chain === 'arbitrum' && a.custody === 'wallet')
        .map((a) => a.address.toLowerCase());

      if (arbitrumAddresses.length === 0) return [];

      // Find wallets that match the filtered addresses
      const matchingWallets = wallets.filter((wallet) =>
        arbitrumAddresses.includes(wallet.address.toLowerCase())
      );

      // Create position balances for each matching wallet
      const hyperliquidBalances = matchingWallets.flatMap((wallet) => {
        const marginSummary = marginSummaryById[wallet.walletId];

        if (!marginSummary || !wallet.address) return [];
        return mapHyperliquidAccountToBalance(marginSummary, wallet.address);
      });

      return hyperliquidBalances;
    },
    [wallets, marginSummaryById]
  );

  return (
    <HyperliquidBalancesContext.Provider value={{ getHyperliquidBalances }}>
      {children}
    </HyperliquidBalancesContext.Provider>
  );
}

export function useHyperliquidBalances() {
  const context = useContext(HyperliquidBalancesContext);
  if (!context) {
    throw new Error(
      'useHyperliquidMarginSummary must be used within HyperliquidMarginSummaryContext'
    );
  }
  return context;
}
